'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { getStoreConfiguration } from '@core/utils/store';
import { WESOG_STORE_CONFIGURATION_PARAMS } from '@core/constants/STORE';
import { isVideoItem } from '@core/utils/active';
import {
    ACTIVE_ID_ATTRIBUTE,
    CUSTOM_FLEX_GROW_ATTRIBUTE,
    CURRENT_WESOG_PROVIDER_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
} from '@core/constants/ATTRIBUTES';
import {
    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
    TOOLTIP_POSITION_ATTRIBUTE,
    BOUND_POSITION_ATTRIBUTE,
    BOUND_POSITIONS,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

/**
 * Handle the description active configuration.
 * @param {HTMLElement} shadowRoot - The shadow root of the component.
 * @returns {void}
 */
export const handleDescriptionActive = (shadowRoot) => {
    const descriptionActiveConfigValue =
        getStoreConfiguration()[WESOG_STORE_CONFIGURATION_PARAMS.DESCRIPTION_ACTIVE];

    const imgTitleDescription = shadowRoot.querySelector('.img-title-description');

    if (descriptionActiveConfigValue) {
        imgTitleDescription.classList.add('img-title-description-show');
    } else {
        imgTitleDescription.classList.remove('img-title-description-show');
    }
};

/**
 * Translate the component elements.
 * @param {HTMLElement} shadowRoot - The shadow root of the component.
 * @param {string} currentLanguage - The current language.
 * @param {string} defaultLanguage - The default language.
 * @returns {void}
 */
export const handleTranslationHelper = (shadowRoot, currentLanguage, defaultLanguage) => {
    updateUIFromShadowRoot(shadowRoot);

    const exclusiveImg = shadowRoot.querySelector('.exclusive-watermark-img');
    if (exclusiveImg) {
        exclusiveImg.src = `assets/img/exclusive_watermark_${
            currentLanguage || defaultLanguage
        }.svg`;
    }
};

/**
 * Handle video item configuration.
 * @param {HTMLElement} shadowRoot - The shadow root of the component.
 * @param {Object} img - The image object.
 * @returns {void}
 */
export const handleVideoItemConfiguration = (shadowRoot, img) => {
    if (!isVideoItem(img.img_zoom)) {
        return;
    }

    const [videoCaptionText, videoCaptionTextDescription] = img?.caption
        ? img.caption.split('|')
        : ['', ''];

    if (videoCaptionTextDescription) {
        const videoCaptionElement = shadowRoot.querySelector('[data-video-caption]');
        videoCaptionElement.style.display = '-webkit-box';
        videoCaptionElement.innerText = videoCaptionTextDescription;

        const descriptionElement = shadowRoot.querySelector('.img-title-description > p');
        descriptionElement.innerText = videoCaptionText;
    }

    shadowRoot
        .querySelector('.img-title-description')
        .classList.add('img-title-description-search-img-card');
};

/**
 * Configure icon attributes for lightbox items.
 * @param {HTMLElement} shadowRoot - The shadow root of the component.
 * @param {Object} img - The image object.
 * @param {number} customFlexGrow - The custom flex grow value.
 * @param {number} tooltipContentWidth - The tooltip content width.
 * @param {string} imgUid - The image UID.
 * @param {boolean} isLightboxItem - Whether this is a lightbox item.
 * @returns {void}
 */
export const configureIconAttributes = (
    shadowRoot,
    img,
    customFlexGrow,
    tooltipContentWidth,
    imgUid,
    isLightboxItem,
) => {
    const zoomModalIcon = shadowRoot.querySelector('[data-zoom-modal-icon]');
    const imgDeleteIcon = shadowRoot.querySelector('[data-delete-icon]');
    const imgStarIcon = shadowRoot.querySelector('[data-star-icon]');
    const downloadIcon = shadowRoot.querySelector('[data-download-icon]');
    const imgAddCartIcon = shadowRoot.querySelector('[data-add-cart-icon]');
    const imgLightboxIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');
    const generativeAIIcon = shadowRoot.querySelector('[data-generative-ai-icon]');
    const premiumIcon = shadowRoot.querySelector('[data-premium-icon]');
    const imgSimilarImagesContainer = shadowRoot.querySelector(
        '[data-similar-images-icon]',
    );

    const elements = [
        zoomModalIcon,
        downloadIcon,
        imgAddCartIcon,
        imgLightboxIcon,
        generativeAIIcon,
        premiumIcon,
        imgSimilarImagesContainer,
    ];

    elements.forEach((element) => {
        if (element) {
            element.setAttribute(ACTIVE_ID_ATTRIBUTE, imgUid);
            element.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
            element.setAttribute(TOOLTIP_CONTENT_WIDTH_ATTRIBUTE, tooltipContentWidth);
        }
    });

    if (isLightboxItem) {
        if (imgDeleteIcon) {
            imgDeleteIcon.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
            imgDeleteIcon.setAttribute(
                TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                tooltipContentWidth,
            );
            imgDeleteIcon.setAttribute(ACTIVE_ID_ATTRIBUTE, imgUid);
            imgDeleteIcon.setAttribute('data-lightbox-item-type', 'lightbox_img');
        }

        if (imgStarIcon) {
            imgStarIcon.setAttribute(DETAILS_VIEW_ATTRIBUTE_IMG, JSON.stringify(img));
            imgStarIcon.setAttribute(CUSTOM_FLEX_GROW_ATTRIBUTE, customFlexGrow);
            imgStarIcon.setAttribute(
                TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
                tooltipContentWidth,
            );
        }
    } else {
        if (imgDeleteIcon) imgDeleteIcon.style.display = 'none';
        if (imgStarIcon) imgStarIcon.style.display = 'none';
    }

    if (downloadIcon && imgSimilarImagesContainer) {
        downloadIcon.setAttribute(
            CURRENT_WESOG_PROVIDER_ATTRIBUTE,
            img.wesog_provider || 'GTRES',
        );
        imgSimilarImagesContainer.setAttribute(
            CURRENT_WESOG_PROVIDER_ATTRIBUTE,
            img.wesog_provider || 'GTRES',
        );
    }

    if (imgLightboxIcon && imgSimilarImagesContainer) {
        const detailsData = {
            caption: img.caption,
            img_src: img.img_src,
            img_width: img.img_width,
        };
        imgLightboxIcon.setAttribute(
            DETAILS_VIEW_ATTRIBUTE_IMG,
            JSON.stringify(detailsData),
        );
        imgSimilarImagesContainer.setAttribute(
            DETAILS_VIEW_ATTRIBUTE_IMG,
            JSON.stringify(detailsData),
        );
    }
};

/**
 * Configure icon containers for different flex grow values.
 * @param {HTMLElement} shadowRoot - The shadow root of the component.
 * @param {number} customFlexGrow - The custom flex grow value.
 * @param {number} maxFlexGrowValue - The maximum flex grow value.
 * @param {number} maxLightboxFlexGrowValue - The maximum lightbox flex grow value.
 * @param {boolean} isLightboxItem - Whether this is a lightbox item.
 * @returns {void}
 */
export const configureIconContainers = (
    shadowRoot,
    customFlexGrow,
    maxFlexGrowValue,
    maxLightboxFlexGrowValue,
    isLightboxItem,
) => {
    const imgFavIconTooltipContainers = shadowRoot.querySelectorAll('[data-img-icons]');
    const zoomModalIcon = shadowRoot.querySelector('[data-zoom-modal-icon]');
    const imgAddCartIcon = shadowRoot.querySelector('[data-add-cart-icon]');
    const imgLightboxIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');
    const downloadIcon = shadowRoot.querySelector('[data-download-icon]');
    const imgStarIcon = shadowRoot.querySelector('[data-star-icon]');
    const imgDeleteIcon = shadowRoot.querySelector('[data-delete-icon]');

    if (isLightboxItem && customFlexGrow < maxLightboxFlexGrowValue) {
        Array.from(imgFavIconTooltipContainers).forEach((container) => {
            container.style.maxWidth = '50px';
            container.style.flexDirection = 'column';
            container.style.height = 'fit-content';
            container.style.maxHeight = '90%';
        });

        setTimeout(() => {
            [
                zoomModalIcon,
                imgStarIcon,
                imgDeleteIcon,
                downloadIcon,
                imgAddCartIcon,
                imgLightboxIcon,
            ].forEach((element) => {
                if (element) {
                    element.setAttribute(
                        TOOLTIP_POSITION_ATTRIBUTE,
                        TOOLTIP_POSITION.TOP_LEFT,
                    );
                }
            });
        }, 0);
    }

    if (customFlexGrow < maxFlexGrowValue) {
        Array.from(imgFavIconTooltipContainers).forEach((container) => {
            container.style.maxWidth = '40px';
            container.style.flexDirection = 'column';
            container.style.maxHeight = '90%';
            container.style.height = 'fit-content';
        });

        setTimeout(() => {
            [zoomModalIcon, imgAddCartIcon, imgLightboxIcon, downloadIcon].forEach(
                (element) => {
                    if (element) {
                        element.setAttribute(
                            TOOLTIP_POSITION_ATTRIBUTE,
                            TOOLTIP_POSITION.TOP_LEFT,
                        );
                    }
                },
            );

            if (imgLightboxIcon) {
                imgLightboxIcon.setAttribute(
                    BOUND_POSITION_ATTRIBUTE,
                    BOUND_POSITIONS.LEFT,
                );
            }
        }, 0);
    } else {
        Array.from(imgFavIconTooltipContainers).forEach((container) => {
            container.style.maxWidth = '300px';
            container.style.flexDirection = 'row';
            container.style.maxHeight = '50px';
        });

        setTimeout(() => {
            [zoomModalIcon, imgAddCartIcon, imgLightboxIcon, downloadIcon].forEach(
                (element) => {
                    if (element) {
                        element.setAttribute(
                            TOOLTIP_POSITION_ATTRIBUTE,
                            TOOLTIP_POSITION.BOTTOM_RIGHT,
                        );
                    }
                },
            );
        }, 0);
    }
};
